import { Button, cn } from "@cronika/ui";
import { Down, Up } from "@cronika/assets/icons";
import { FC, type RefObject, useRef, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";
import type { MutableRefObject } from "react";

import { isLessThanCurrentTime, nowPlusOneMinute, pad2, type THms } from "./lib";

type TStepperProps = {
  value: number;
  label: string;
  onIncrement: () => void;
  onDecrement: () => void;
  /** Direct entry — receives an already clamped value in [0, max). */
  onChange?: (next: number) => void;
  /** Exclusive upper bound for typed input (24 for hours, 60 for min/sec). */
  max: number;
  /** Renders the value in the error colour (e.g. expiry in the past). */
  invalid?: boolean;
  disabled?: boolean;
  className?: string;
  /**
   * Time-mode only: full wall clock + which field this plate edits. Used on
   * outside-click to snap a past draft to now + 1 minute.
   */
  compareToNow?: boolean;
  hms?: THms;
  field?: keyof THms;
  /** Replaces the whole HMS (used when clamping a past time). */
  onReplace?: (next: THms) => void;
  /** IANA timezone for wall-clock comparisons (falls back to browser local when empty). */
  timezone?: string;
  /** Ref exposed so a parent can programmatically focus this input. */
  inputRef?: MutableRefObject<HTMLInputElement | null>;
  /** Called after the user finishes entering 2 digits, so focus can jump to the next field. */
  onAutoAdvance?: () => void;
  /** Called when this input receives focus, so the parent can track direction. */
  onFocusField?: () => void;
};

/**
 * A single dark "digit plate" with an up-chevron above and down-chevron below,
 * showing a zero-padded value and a caption. Shared by every H / M / S slot of
 * the manual stepper so both modes look identical.
 *
 * The value is also directly typable: while the field is focused the digits are
 * highlighted (warning colour + underline) so it reads as an active edit, and
 * typed input is clamped to the field's range.
 */
export const Stepper: FC<TStepperProps> = ({
  value,
  label,
  onIncrement,
  onDecrement,
  onChange,
  max,
  invalid,
  disabled,
  className,
  compareToNow,
  hms,
  field,
  onReplace,
  timezone = "",
  inputRef,
  onAutoAdvance,
  onFocusField,
}) => {
  // Non-null while the user is typing; keeps partial input (e.g. "0") visible
  // instead of snapping to the padded committed value on every keystroke.
  const [draft, setDraft] = useState<string | null>(null);
  const isEditing = draft !== null;
  const rootRef = useRef<HTMLDivElement>(null);
  const internalInputRef = useRef<HTMLInputElement>(null);
  // Merge external inputRef with our internal one
  const resolvedInputRef = (el: HTMLInputElement | null) => {
    internalInputRef.current = el;
    if (inputRef) inputRef.current = el;
  };

  // End the edit session on outside click (not blur), so +/- buttons inside
  // this plate don't clear the draft / warning underline mid-interaction.
  // In Time mode, a past draft is snapped to now + 1 minute.
  useOnClickOutside(rootRef as RefObject<HTMLDivElement>, () => {
    if (draft === null) return;

    if (compareToNow && hms && field) {
      const edited = draft === "" ? value : Math.min(Number(draft), max - 1);
      const next: THms = { ...hms, [field]: edited };
      if (isLessThanCurrentTime(next, new Date(), timezone)) {
        onReplace?.(nowPlusOneMinute(new Date(), timezone));
      }
    }

    setDraft(null);
  });

  const handleChange = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(-2);
    setDraft(digits);
    if (digits !== "") {
      onChange?.(Math.min(Number(digits), max - 1));
      // Auto-advance to the next field once 2 digits are entered.
      if (digits.length === 2) {
        setDraft(null);
        onAutoAdvance?.();
      }
    }
  };

  return (
    <div ref={rootRef}>
      <div
        className={cn(
          "flex flex-col items-center rounded-2xl bg-glass/4 border border-glass/10 overflow-hidden",
          invalid && "border-danger/60",
          className,
        )}
      >
        <Button
          type="button"
          className="flex h-7 w-full items-center justify-center rounded-none bg-transparent text-default-500 transition-colors hover:bg-warning/10 hover:text-warning disabled:opacity-50"
          onClick={onIncrement}
          disabled={disabled}
          aria-label={`increase ${label}`}
        >
          <Up className="h-4 w-4" />
        </Button>
        <div className="bg-glass/4 flex flex-col items-center justify-center py-2 w-full">
          <input
            ref={resolvedInputRef}
            type="text"
            inputMode="numeric"
            aria-label={label}
            disabled={disabled}
            value={isEditing ? draft : pad2(value)}
            onFocus={(e) => {
              onFocusField?.();
              setDraft(pad2(value));
              e.target.select();
            }}
            onChange={(e) => handleChange(e.target.value)}
            className={cn(
              "w-full bg-transparent text-center text-[20px] font-semibold leading-none tabular-nums outline-none",
              invalid ? "text-danger" : isEditing ? "text-warning underline underline-offset-4" : "text-white",
            )}
          />
          <span className="text-[10px] uppercase tracking-wide text-default-500">{label}</span>
        </div>
        <Button
          type="button"
          className="flex h-7 w-full items-center justify-center rounded-none bg-transparent text-default-500 transition-colors hover:bg-warning/10 hover:text-warning disabled:opacity-50"
          onClick={onDecrement}
          disabled={disabled}
          aria-label={`decrease ${label}`}
        >
          <Down className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
